import { useState } from 'react';
import { useTranslation } from '@frontend/i18n';
import { Icon } from '@frontend/icons';
import { pendo } from '@frontend/tracking';
import { Chip, Heading, NakedButton, Text } from '@frontend/design-system';
import { styles } from '../styles';
import { InvoiceCardDetailsProps } from '../types';
import { RecentActivityList } from './recent-activity-list';

export const InvoiceCardDetails = ({
  className,
  children,
  invoice,
  formattedBilledAt,
  billedAtData,
  formattedBilledAmount,
  formattedOwedAmount,
}: InvoiceCardDetailsProps) => {
  const { t } = useTranslation();
  const [isActivityOpen, setIsActivityOpen] = useState(false);

  const handleToggleActivity = () => {
    pendo.track('invoice_card_toggle_activity', {
      invoiceId: invoice?.id,
      locationId: invoice?.locationId,
    });
    setIsActivityOpen((prev) => !prev);
  };

  return (
    <div
      className={`card ${className || ''}`}
      css={styles.card}
      data-trackingid={invoice ? `invoice-card-${invoice.id}` : 'invoice-card'}
    >
      <div className='card__header' css={styles.header}>
        <div className='card__info-block' css={styles.infoBlock}>
          <Text size='small' color='light'>
            {t('Outstanding Amount')}
          </Text>
          <Heading level={2}>{formattedBilledAmount}</Heading>
        </div>
        <div className='card__date-chip'>
          <Chip maxWidth='150px' variant={billedAtData.variant}>
            {billedAtData.daysPassedLabel}
          </Chip>
        </div>
      </div>
      <div className='card__info-blocks' css={styles.infoBlocks(true)}>
        <InfoBlock label={t('Balance Created')} value={formattedBilledAt} />
        <InfoBlock label={t('Amount Owed')} value={formattedOwedAmount ?? ''} />
        <InfoBlock label={t('Provider')} value={invoice.providerName || '-'} />
      </div>
      <div className='card__recent-activity' css={styles.recentActivities}>
        <NakedButton
          css={styles.recentActivityButton}
          onClick={handleToggleActivity}
          trackingId='invoice-card-recent-activity-button'
        >
          <Text size='small'>{t('View recent activity')}</Text>
          <Icon name={isActivityOpen ? 'caret-up-tiny' : 'caret-down-tiny'} size={8} color='subdued' />
        </NakedButton>
        {isActivityOpen && <RecentActivityList invoiceId={invoice?.id} locationId={invoice?.locationId} />}
      </div>
      <div className='card__body'>{children}</div>
    </div>
  );
};

const InfoBlock = ({ label, value }: { label: string; value: string }) => (
  <div className='card__info-block' css={styles.infoBlock}>
    <Text size='small' color='light'>
      {label}
    </Text>
    <Text size='medium'>{value}</Text>
  </div>
);
